import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, Trash2, Pencil, Settings, Users, Folders, Link2,
  Loader2, Save, X, Building2, Package, AlertTriangle, Check, RefreshCw, HardDrive,
  Monitor, Copy, Ban, PlayCircle, Key, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('types');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [types, setTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [families, setFamilies] = useState([]);
  const [kiosks, setKiosks] = useState([]);
  
  // R2 Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  
  // Dialog states
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [showFamilyDialog, setShowFamilyDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showKioskDialog, setShowKioskDialog] = useState(false);
  const [showKioskTokenDialog, setShowKioskTokenDialog] = useState(false);
  
  // Form states
  const [editingType, setEditingType] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [editingFamily, setEditingFamily] = useState(null);
  const [linkingType, setLinkingType] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedKiosk, setSelectedKiosk] = useState(null);
  
  // Form data
  const [typeForm, setTypeForm] = useState({ code: '', name: '', has_revision: true });
  const [clientForm, setClientForm] = useState({ code: '', name: '' });
  const [familyForm, setFamilyForm] = useState({ code: '', name: '' });
  const [linkForm, setLinkForm] = useState({ family_codes: [], client_codes: [] });
  const [kioskForm, setKioskForm] = useState({ name: '', description: '' });
  
  // Loading states
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTypes = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/admin/types`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      setTypes(response.data);
    } catch (error) {
      console.error('Error fetching types:', error);
      toast.error('Erro ao carregar tipos');
    }
  }, []);

  const fetchClients = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/admin/clients`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Erro ao carregar clientes');
    }
  }, []);

  const fetchFamilies = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/admin/families`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      setFamilies(response.data);
    } catch (error) {
      console.error('Error fetching families:', error);
      toast.error('Erro ao carregar famílias');
    }
  }, []);

  const fetchKiosks = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/admin/kiosks`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      setKiosks(response.data.kiosks || []);
    } catch (error) {
      console.error('Error fetching kiosks:', error);
      toast.error('Erro ao carregar kiosks');
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchTypes(), fetchClients(), fetchFamilies(), fetchKiosks()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchTypes, fetchClients, fetchFamilies, fetchKiosks]);

  // ============ KIOSK HANDLERS ============

  const handleCreateKiosk = async () => {
    if (!kioskForm.name.trim()) {
      toast.error('Nome do kiosk é obrigatório');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/kiosks`, kioskForm, { headers });
      setKiosks([response.data, ...kiosks]);
      setShowKioskDialog(false);
      setKioskForm({ name: '', description: '' });
      toast.success('Kiosk criado com sucesso!');
      
      // Show token dialog
      setSelectedKiosk(response.data);
      setShowKioskTokenDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar kiosk');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeKiosk = async (kiosk) => {
    try {
      await axios.post(`${API_URL}/api/admin/kiosks/${kiosk.id}/revoke`, {}, { headers });
      fetchKiosks();
      toast.success(`Kiosk "${kiosk.name}" revogado`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao revogar kiosk');
    }
  };

  const handleReactivateKiosk = async (kiosk) => {
    try {
      await axios.post(`${API_URL}/api/admin/kiosks/${kiosk.id}/reactivate`, {}, { headers });
      fetchKiosks();
      toast.success(`Kiosk "${kiosk.name}" reativado`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reativar kiosk');
    }
  };

  const handleRegenerateToken = async (kiosk) => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/kiosks/${kiosk.id}/regenerate-token`, {}, { headers });
      fetchKiosks();
      setSelectedKiosk({ ...kiosk, token: response.data.token });
      setShowKioskTokenDialog(true);
      toast.success('Token regenerado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao regenerar token');
    }
  };

  const handleDeleteKiosk = async (kiosk) => {
    try {
      await axios.delete(`${API_URL}/api/admin/kiosks/${kiosk.id}`, { headers });
      fetchKiosks();
      toast.success(`Kiosk "${kiosk.name}" excluído`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir kiosk');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Token copiado para a área de transferência!');
  };

  // ============ R2 SYNC HANDLER ============
  
  const handleSyncR2 = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/admin/sync-r2-drawings`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncResult(response.data);
      if (response.data.added_to_existing > 0 || response.data.created_new > 0) {
        toast.success(`Sincronização concluída! ${response.data.added_to_existing + response.data.created_new} arquivos adicionados.`);
      } else {
        toast.info('Todos os arquivos já estão sincronizados!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  // ============ TYPE HANDLERS ============
  
  const openTypeDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setTypeForm({ code: type.code, name: type.name, has_revision: type.has_revision });
    } else {
      setEditingType(null);
      setTypeForm({ code: '', name: '', has_revision: true });
    }
    setShowTypeDialog(true);
  };

  const saveType = async () => {
    if (!typeForm.code || !typeForm.name) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    setSaving(true);
    try {
      if (editingType) {
        await axios.put(`${API_URL}/api/admin/types/${editingType.code}`, 
          { name: typeForm.name, has_revision: typeForm.has_revision }, 
          { headers }
        );
        toast.success('Tipo atualizado com sucesso');
      } else {
        await axios.post(`${API_URL}/api/admin/types`, 
          { code: parseInt(typeForm.code), name: typeForm.name, has_revision: typeForm.has_revision }, 
          { headers }
        );
        toast.success('Tipo criado com sucesso');
      }
      setShowTypeDialog(false);
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar tipo');
    } finally {
      setSaving(false);
    }
  };

  // ============ CLIENT HANDLERS ============
  
  const openClientDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setClientForm({ code: client.code, name: client.name });
    } else {
      setEditingClient(null);
      setClientForm({ code: '', name: '' });
    }
    setShowClientDialog(true);
  };

  const saveClient = async () => {
    if (!clientForm.code || !clientForm.name) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    const code = parseInt(clientForm.code);
    if (code < 10 || code > 99) {
      toast.error('Código de cliente deve estar entre 10 e 99');
      return;
    }
    
    setSaving(true);
    try {
      if (editingClient) {
        await axios.put(`${API_URL}/api/admin/clients/${editingClient.code}`, 
          { name: clientForm.name }, 
          { headers }
        );
        toast.success('Cliente atualizado com sucesso');
      } else {
        await axios.post(`${API_URL}/api/admin/clients`, 
          { code, name: clientForm.name }, 
          { headers }
        );
        toast.success('Cliente criado com sucesso');
      }
      setShowClientDialog(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cliente');
    } finally {
      setSaving(false);
    }
  };

  // ============ FAMILY HANDLERS ============
  
  const openFamilyDialog = (family = null) => {
    if (family) {
      setEditingFamily(family);
      setFamilyForm({ code: family.code, name: family.name });
    } else {
      setEditingFamily(null);
      setFamilyForm({ code: '', name: '' });
    }
    setShowFamilyDialog(true);
  };

  const saveFamily = async () => {
    if (!familyForm.code || !familyForm.name) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    const code = parseInt(familyForm.code);
    if (code < 100) {
      toast.error('Código de família deve ser >= 100');
      return;
    }
    
    setSaving(true);
    try {
      if (editingFamily) {
        await axios.put(`${API_URL}/api/admin/families/${editingFamily.code}`, 
          { name: familyForm.name }, 
          { headers }
        );
        toast.success('Família atualizada com sucesso');
      } else {
        await axios.post(`${API_URL}/api/admin/families`, 
          { code, name: familyForm.name }, 
          { headers }
        );
        toast.success('Família criada com sucesso');
      }
      setShowFamilyDialog(false);
      fetchFamilies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar família');
    } finally {
      setSaving(false);
    }
  };

  // ============ LINK HANDLERS ============
  
  const openLinkDialog = (type) => {
    setLinkingType(type);
    setLinkForm({
      family_codes: type.allowed_families || [],
      client_codes: type.allowed_clients || []
    });
    setShowLinkDialog(true);
  };

  const toggleFamilyLink = (familyCode) => {
    setLinkForm(prev => ({
      ...prev,
      family_codes: prev.family_codes.includes(familyCode)
        ? prev.family_codes.filter(c => c !== familyCode)
        : [...prev.family_codes, familyCode]
    }));
  };

  const toggleClientLink = (clientCode) => {
    setLinkForm(prev => ({
      ...prev,
      client_codes: prev.client_codes.includes(clientCode)
        ? prev.client_codes.filter(c => c !== clientCode)
        : [...prev.client_codes, clientCode]
    }));
  };

  const saveLinks = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/types/${linkingType.code}/links`, 
        { 
          type_code: linkingType.code,
          family_codes: linkForm.family_codes,
          client_codes: linkForm.client_codes
        }, 
        { headers }
      );
      toast.success('Vinculações salvas com sucesso');
      setShowLinkDialog(false);
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar vinculações');
    } finally {
      setSaving(false);
    }
  };

  // ============ DELETE HANDLERS ============
  
  const openDeleteDialog = (item, type) => {
    setDeleteTarget({ item, type });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      const { item, type } = deleteTarget;
      let url = '';
      
      switch (type) {
        case 'type':
          url = `${API_URL}/api/admin/types/${item.code}`;
          break;
        case 'client':
          url = `${API_URL}/api/admin/clients/${item.code}`;
          break;
        case 'family':
          url = `${API_URL}/api/admin/families/${item.code}`;
          break;
        default:
          return;
      }
      
      await axios.delete(url, { headers });
      toast.success('Item excluído com sucesso');
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      
      // Refresh data
      if (type === 'type') fetchTypes();
      if (type === 'client') fetchClients();
      if (type === 'family') fetchFamilies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir item');
    } finally {
      setDeleting(false);
    }
  };

  // Check admin access
  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
              <p className="text-slate-500">Esta página é exclusiva para administradores.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl" data-testid="admin-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <Settings className="w-7 h-7 text-blue-600" strokeWidth={1.5} />
              Painel de Administração
            </h1>
            <p className="text-slate-500 mt-1">
              Gerencie tipos, clientes e famílias do sistema
            </p>
          </div>
          
          {/* R2 Sync Button */}
          <Button
            onClick={handleSyncR2}
            disabled={syncing}
            variant="outline"
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            data-testid="sync-r2-btn"
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Sincronizar Desenhos (R2)
          </Button>
        </div>

        {/* Sync Result */}
        {syncResult && (
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <HardDrive className="w-5 h-5 text-purple-600" />
                <span className="font-medium text-purple-900">Resultado da Sincronização</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div className="bg-white rounded-lg p-2 text-center">
                  <p className="text-xl font-bold text-purple-600">{syncResult.total_r2_files}</p>
                  <p className="text-xs text-slate-500">Arquivos no R2</p>
                </div>
                <div className="bg-white rounded-lg p-2 text-center">
                  <p className="text-xl font-bold text-green-600">{syncResult.added_to_existing}</p>
                  <p className="text-xs text-slate-500">Adicionados</p>
                </div>
                <div className="bg-white rounded-lg p-2 text-center">
                  <p className="text-xl font-bold text-blue-600">{syncResult.created_new}</p>
                  <p className="text-xs text-slate-500">Novos</p>
                </div>
                <div className="bg-white rounded-lg p-2 text-center">
                  <p className="text-xl font-bold text-slate-400">{syncResult.skipped_invalid}</p>
                  <p className="text-xs text-slate-500">Ignorados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="types" className="flex items-center gap-2" data-testid="types-tab">
              <Package className="w-4 h-4" />
              Tipos
            </TabsTrigger>
            <TabsTrigger value="clients" className="flex items-center gap-2" data-testid="clients-tab">
              <Building2 className="w-4 h-4" />
              Clientes
            </TabsTrigger>
            <TabsTrigger value="families" className="flex items-center gap-2" data-testid="families-tab">
              <Folders className="w-4 h-4" />
              Famílias
            </TabsTrigger>
            <TabsTrigger value="kiosks" className="flex items-center gap-2" data-testid="kiosks-tab">
              <Monitor className="w-4 h-4" />
              Kiosks
            </TabsTrigger>
          </TabsList>

          {/* Types Tab */}
          <TabsContent value="types" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Tipos de Produto</CardTitle>
                  <CardDescription>Primeiro dígito do código (1-9)</CardDescription>
                </div>
                <Button onClick={() => openTypeDialog()} data-testid="add-type-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Tipo
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-20">Código</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead className="w-24 text-center">Revisão</TableHead>
                        <TableHead className="w-40">Vinculações</TableHead>
                        <TableHead className="w-32 text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {types.map((type) => (
                        <TableRow key={type.code} data-testid={`type-row-${type.code}`}>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">{type.code}</Badge>
                          </TableCell>
                          <TableCell className="font-medium">{type.name}</TableCell>
                          <TableCell className="text-center">
                            {type.has_revision ? (
                              <Check className="w-4 h-4 text-green-600 mx-auto" />
                            ) : (
                              <X className="w-4 h-4 text-slate-300 mx-auto" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openLinkDialog(type)}
                              className="h-7 text-blue-600"
                            >
                              <Link2 className="w-3 h-3 mr-1" />
                              {(type.allowed_families?.length || 0) + (type.allowed_clients?.length || 0) || 'Todas'}
                            </Button>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openTypeDialog(type)}
                              className="h-8 w-8 p-0"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(type, 'type')}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Clientes</CardTitle>
                  <CardDescription>Códigos de 10 a 99</CardDescription>
                </div>
                <Button onClick={() => openClientDialog()} data-testid="add-client-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Cliente
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-20">Código</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead className="w-32 text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clients.map((client) => (
                        <TableRow key={client.code} data-testid={`client-row-${client.code}`}>
                          <TableCell>
                            <Badge variant="secondary" className="font-mono">{client.code}</Badge>
                          </TableCell>
                          <TableCell className="font-medium">{client.name}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openClientDialog(client)}
                              className="h-8 w-8 p-0"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(client, 'client')}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Families Tab */}
          <TabsContent value="families" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Famílias</CardTitle>
                  <CardDescription>Códigos a partir de 100</CardDescription>
                </div>
                <Button onClick={() => openFamilyDialog()} data-testid="add-family-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Família
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-20">Código</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead className="w-32 text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {families.map((family) => (
                        <TableRow key={family.code} data-testid={`family-row-${family.code}`}>
                          <TableCell>
                            <Badge className="font-mono bg-blue-100 text-blue-700">{family.code}</Badge>
                          </TableCell>
                          <TableCell className="font-medium">{family.name}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openFamilyDialog(family)}
                              className="h-8 w-8 p-0"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(family, 'family')}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Kiosks Tab */}
          <TabsContent value="kiosks" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Kiosks da Fábrica</CardTitle>
                  <CardDescription>Terminais de consulta de desenhos com acesso restrito por IP</CardDescription>
                </div>
                <Button onClick={() => {
                  setKioskForm({ name: '', description: '' });
                  setShowKioskDialog(true);
                }} data-testid="new-kiosk-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Kiosk
                </Button>
              </CardHeader>
              <CardContent>
                {kiosks.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Monitor className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Nenhum kiosk cadastrado</p>
                    <p className="text-sm mt-1">Crie um kiosk para permitir consulta de desenhos na fábrica</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Último Acesso</TableHead>
                        <TableHead>Acessos</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {kiosks.map((kiosk) => (
                        <TableRow key={kiosk.id}>
                          <TableCell className="font-medium">{kiosk.name}</TableCell>
                          <TableCell className="text-slate-500">{kiosk.description || '-'}</TableCell>
                          <TableCell>
                            {kiosk.status === 'active' ? (
                              <Badge className="bg-green-100 text-green-700">Ativo</Badge>
                            ) : (
                              <Badge variant="destructive">Revogado</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm">
                            {kiosk.last_access 
                              ? new Date(kiosk.last_access).toLocaleString('pt-BR')
                              : 'Nunca'}
                          </TableCell>
                          <TableCell>{kiosk.access_count || 0}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedKiosk(kiosk);
                                  setShowKioskTokenDialog(true);
                                }}
                                title="Ver Token"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRegenerateToken(kiosk)}
                                title="Regenerar Token"
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              {kiosk.status === 'active' ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRevokeKiosk(kiosk)}
                                  className="text-orange-600 hover:text-orange-700"
                                  title="Revogar"
                                >
                                  <Ban className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReactivateKiosk(kiosk)}
                                  className="text-green-600 hover:text-green-700"
                                  title="Reativar"
                                >
                                  <PlayCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteKiosk(kiosk)}
                                className="text-red-600 hover:text-red-700"
                                title="Excluir"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Kiosk Dialog */}
        <Dialog open={showKioskDialog} onOpenChange={setShowKioskDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Novo Kiosk</DialogTitle>
              <DialogDescription>
                Crie um kiosk para acesso aos desenhos na fábrica
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nome do Kiosk *</Label>
                <Input
                  value={kioskForm.name}
                  onChange={(e) => setKioskForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Produção 01, Almoxarifado"
                  className="mt-1"
                  data-testid="kiosk-name-input"
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Input
                  value={kioskForm.description}
                  onChange={(e) => setKioskForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Ex: Terminal na área de soldagem"
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowKioskDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateKiosk} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Criar Kiosk
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Kiosk Token Dialog */}
        <Dialog open={showKioskTokenDialog} onOpenChange={setShowKioskTokenDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Token do Kiosk: {selectedKiosk?.name}</DialogTitle>
              <DialogDescription>
                Use este token para ativar o kiosk. O token é único e pode ser regenerado se necessário.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-100 rounded-lg">
                <Label className="text-xs text-slate-500 mb-2 block">Token de Ativação</Label>
                <code className="text-sm break-all text-slate-700 font-mono">
                  {selectedKiosk?.token}
                </code>
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => copyToClipboard(selectedKiosk?.token)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Token
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => copyToClipboard(`${window.location.origin}/kiosk`)}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar URL
                </Button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Como usar:</h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>No computador do kiosk, acesse: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/kiosk</code></li>
                  <li>Cole o token de ativação</li>
                  <li>Pronto! O kiosk pode buscar e visualizar desenhos</li>
                </ol>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-medium text-amber-900 mb-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Segurança
                </h4>
                <p className="text-sm text-amber-800">
                  Configure o firewall do servidor para restringir o acesso 
                  à rota <code className="bg-amber-100 px-1 rounded">/kiosk</code> apenas aos IPs autorizados.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowKioskTokenDialog(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Type Dialog */}
        <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingType ? 'Editar Tipo' : 'Novo Tipo'}</DialogTitle>
              <DialogDescription>
                {editingType ? 'Atualize as informações do tipo' : 'Crie um novo tipo de produto'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Código</Label>
                <Input
                  type="number"
                  value={typeForm.code}
                  onChange={(e) => setTypeForm(prev => ({ ...prev, code: e.target.value }))}
                  disabled={!!editingType}
                  placeholder="Ex: 1"
                  className="mt-1"
                  data-testid="type-code-input"
                />
              </div>
              <div>
                <Label>Nome</Label>
                <Input
                  value={typeForm.name}
                  onChange={(e) => setTypeForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Produto Acabado"
                  className="mt-1"
                  data-testid="type-name-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="has_revision"
                  checked={typeForm.has_revision}
                  onCheckedChange={(checked) => setTypeForm(prev => ({ ...prev, has_revision: checked }))}
                />
                <Label htmlFor="has_revision" className="cursor-pointer">
                  Usa revisão no código
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTypeDialog(false)}>Cancelar</Button>
              <Button onClick={saveType} disabled={saving} data-testid="save-type-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Client Dialog */}
        <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingClient ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
              <DialogDescription>
                {editingClient ? 'Atualize as informações do cliente' : 'Códigos de cliente: 10 a 99'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Código (10-99)</Label>
                <Input
                  type="number"
                  min="10"
                  max="99"
                  value={clientForm.code}
                  onChange={(e) => setClientForm(prev => ({ ...prev, code: e.target.value }))}
                  disabled={!!editingClient}
                  placeholder="Ex: 15"
                  className="mt-1"
                  data-testid="client-code-input"
                />
              </div>
              <div>
                <Label>Nome</Label>
                <Input
                  value={clientForm.name}
                  onChange={(e) => setClientForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: NCR"
                  className="mt-1"
                  data-testid="client-name-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowClientDialog(false)}>Cancelar</Button>
              <Button onClick={saveClient} disabled={saving} data-testid="save-client-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Family Dialog */}
        <Dialog open={showFamilyDialog} onOpenChange={setShowFamilyDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingFamily ? 'Editar Família' : 'Nova Família'}</DialogTitle>
              <DialogDescription>
                {editingFamily ? 'Atualize as informações da família' : 'Códigos de família: 100 ou mais'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Código (100+)</Label>
                <Input
                  type="number"
                  min="100"
                  value={familyForm.code}
                  onChange={(e) => setFamilyForm(prev => ({ ...prev, code: e.target.value }))}
                  disabled={!!editingFamily}
                  placeholder="Ex: 150"
                  className="mt-1"
                  data-testid="family-code-input"
                />
              </div>
              <div>
                <Label>Nome</Label>
                <Input
                  value={familyForm.name}
                  onChange={(e) => setFamilyForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: CHAPAS"
                  className="mt-1"
                  data-testid="family-name-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFamilyDialog(false)}>Cancelar</Button>
              <Button onClick={saveFamily} disabled={saving} data-testid="save-family-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link Dialog */}
        <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-blue-600" />
                Vincular Famílias/Clientes ao Tipo {linkingType?.code} - {linkingType?.name}
              </DialogTitle>
              <DialogDescription>
                Use os botões TODOS/NENHUM para seleção rápida. Apenas os itens selecionados aparecerão no formulário de Criar Código.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              {/* Families */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">
                    Famílias (100+)
                    <Badge variant="secondary" className="ml-2">{linkForm.family_codes.length} selecionadas</Badge>
                  </Label>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs px-2 text-green-700 border-green-300 hover:bg-green-50"
                      onClick={() => setLinkForm(prev => ({ ...prev, family_codes: families.map(f => f.code) }))}
                    >
                      TODOS
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs px-2 text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() => setLinkForm(prev => ({ ...prev, family_codes: [] }))}
                    >
                      NENHUM
                    </Button>
                  </div>
                </div>
                <ScrollArea className="h-64 border rounded-lg p-2">
                  <div className="space-y-1">
                    {families.map((family) => (
                      <div
                        key={family.code}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 ${
                          linkForm.family_codes.includes(family.code) ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => toggleFamilyLink(family.code)}
                      >
                        <Checkbox
                          checked={linkForm.family_codes.includes(family.code)}
                          onCheckedChange={() => toggleFamilyLink(family.code)}
                        />
                        <span className="font-mono text-xs text-slate-500">{family.code}</span>
                        <span className="text-sm">{family.name}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
              
              {/* Clients */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">
                    Clientes (10-99)
                    <Badge variant="secondary" className="ml-2">{linkForm.client_codes.length} selecionados</Badge>
                  </Label>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs px-2 text-green-700 border-green-300 hover:bg-green-50"
                      onClick={() => setLinkForm(prev => ({ ...prev, client_codes: clients.map(c => c.code) }))}
                    >
                      TODOS
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs px-2 text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() => setLinkForm(prev => ({ ...prev, client_codes: [] }))}
                    >
                      NENHUM
                    </Button>
                  </div>
                </div>
                <ScrollArea className="h-64 border rounded-lg p-2">
                  <div className="space-y-1">
                    {clients.map((client) => (
                      <div
                        key={client.code}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-100 ${
                          linkForm.client_codes.includes(client.code) ? 'bg-green-50' : ''
                        }`}
                        onClick={() => toggleClientLink(client.code)}
                      >
                        <Checkbox
                          checked={linkForm.client_codes.includes(client.code)}
                          onCheckedChange={() => toggleClientLink(client.code)}
                        />
                        <span className="font-mono text-xs text-slate-500">{client.code}</span>
                        <span className="text-sm">{client.name}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancelar</Button>
              <Button onClick={saveLinks} disabled={saving} data-testid="save-links-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Save className="w-4 h-4 mr-2" />
                Salvar Vinculações
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Confirmar Exclusão
              </AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir{' '}
                <strong>
                  {deleteTarget?.type === 'type' && `o tipo ${deleteTarget?.item?.code} - ${deleteTarget?.item?.name}`}
                  {deleteTarget?.type === 'client' && `o cliente ${deleteTarget?.item?.code} - ${deleteTarget?.item?.name}`}
                  {deleteTarget?.type === 'family' && `a família ${deleteTarget?.item?.code} - ${deleteTarget?.item?.name}`}
                </strong>
                ?
                <br /><br />
                Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
